import {
  DEFAULT_PHONE_COUNTRY_ISO,
  PHONE_COUNTRIES_LATAM,
  PhoneCountryConfig,
} from './constants/phoneCountries';

export const getPhoneCountryByIso = (iso2: string): PhoneCountryConfig => {
  return (
    PHONE_COUNTRIES_LATAM.find((country) => country.iso2 === iso2) ||
    PHONE_COUNTRIES_LATAM.find((country) => country.iso2 === DEFAULT_PHONE_COUNTRY_ISO)!
  );
};

export const getPhoneCountryByDialCode = (dialCode: string): PhoneCountryConfig | null => {
  return PHONE_COUNTRIES_LATAM.find((country) => country.dialCode === dialCode) || null;
};

export const sanitizePhoneDigits = (value: string | null | undefined): string => {
  if (!value) return '';
  return value.replace(/\D/g, '');
};

export const splitPhoneValue = (
  fullPhone: string | null | undefined,
): { countryIso: string; nationalNumber: string } => {
  const phone = (fullPhone || '').trim();
  if (!phone) {
    return { countryIso: DEFAULT_PHONE_COUNTRY_ISO, nationalNumber: '' };
  }

  const normalized = phone.startsWith('+') ? phone : `+${sanitizePhoneDigits(phone)}`;
  const match = [...PHONE_COUNTRIES_LATAM]
    .sort((a, b) => b.dialCode.length - a.dialCode.length)
    .find((country) => normalized.startsWith(country.dialCode));

  if (!match) {
    return {
      countryIso: DEFAULT_PHONE_COUNTRY_ISO,
      nationalNumber: sanitizePhoneDigits(phone),
    };
  }

  return {
    countryIso: match.iso2,
    nationalNumber: sanitizePhoneDigits(normalized.slice(match.dialCode.length)),
  };
};

export const buildPhoneValue = (countryIso: string, nationalNumber: string): string => {
  const country = getPhoneCountryByIso(countryIso);
  const digits = sanitizePhoneDigits(nationalNumber);
  if (!digits) return '';
  return `${country.dialCode}${digits}`;
};

export const getPhoneLengthHint = (country: PhoneCountryConfig): string => {
  if (country.nationalNumberLengthRange) {
    const [min, max] = country.nationalNumberLengthRange;
    return min === max ? `${min}` : `${min}-${max}`;
  }
  return `${country.nationalNumberLength}`;
};

const isValidPhoneLength = (length: number, country: PhoneCountryConfig): boolean => {
  if (country.nationalNumberLengthRange) {
    const [min, max] = country.nationalNumberLengthRange;
    return length >= min && length <= max;
  }
  return length === country.nationalNumberLength;
};

export const validatePhoneByCountry = (
  countryIso: string,
  nationalNumber: string | null | undefined,
): string | null => {
  const country = getPhoneCountryByIso(countryIso);
  const digits = sanitizePhoneDigits(nationalNumber);

  if (!digits) return null;
  if (!isValidPhoneLength(digits.length, country)) {
    return `El número para ${country.name} debe tener ${getPhoneLengthHint(country)} dígitos`;
  }

  return null;
};

export const isValidFullPhone = (fullPhone: string | null | undefined): boolean => {
  if (!fullPhone) return false;
  const { countryIso, nationalNumber } = splitPhoneValue(fullPhone);
  return validatePhoneByCountry(countryIso, nationalNumber) === null && nationalNumber.length > 0;
};
